import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["phoneInput", "verifyButton", "verifiedBadge", "otpModal", "otpInput", "confirmButton", "resendButton"]
  static values = { isVerified: Boolean }

  connect() {
    const csrfMeta = document.querySelector('[name="csrf-token"]')
    this.csrfToken = csrfMeta ? csrfMeta.getAttribute('content') : ''
    console.log('Phone verification controller connected')
    
    // Store original verified phone for comparison
    this.originalPhone = this.phoneInputTarget.value
    this.originalIsVerified = this.isVerifiedValue
    this.checkVerificationStatus()
    
    // Listen for form submission to clear verification UI
    const form = this.element.closest('form')
    if (form) {
      form.addEventListener('submit', () => this.handleFormSubmit())
    }
  }

  phoneInputChanged() {
    this.checkVerificationStatus()
  }

  checkVerificationStatus() {
    const currentPhone = this.phoneInputTarget.value.trim()
    const phoneChanged = currentPhone !== this.originalPhone
    
    // If phone changed, show verify button and hide verified badge
    if (this.hasVerifyButtonTarget) {
      this.verifyButtonTarget.style.display = (phoneChanged || !this.originalIsVerified) ? 'block' : 'none'
    }
    if (this.hasVerifiedBadgeTarget) {
      this.verifiedBadgeTarget.style.display = (!phoneChanged && this.originalIsVerified) ? 'block' : 'none'
    }
  }

  handleFormSubmit() {
    const currentPhone = this.phoneInputTarget.value.trim()
    if (currentPhone !== this.originalPhone) {
      // Phone changed, so verification will be cleared on backend
      this.originalIsVerified = false
    }
  }

  async initiate(event) {
    event.preventDefault()
    console.log('Initiate verification clicked')
    
    const phoneNumber = this.phoneInputTarget.value.trim()
    console.log('Phone number:', phoneNumber)
    if (!phoneNumber) {
      this.showError("Please enter a phone number")
      return
    }

    this.setLoading(this.verifyButtonTarget, true, "Sending...")

    try {
      const response = await fetch('/profile/verify_phone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ phone_number: phoneNumber })
      })

      const result = await response.json()

      if (result.success) {
        this.showOtpModal()
        this.showSuccess(result.message)
      } else {
        this.showError(result.error)
      }
    } catch (error) {
      this.showError("Network error. Please try again.")
    } finally {
      this.setLoading(this.verifyButtonTarget, false, "Verify")
    }
  }

  async verify(event) {
    event.preventDefault()
    
    const otpCode = this.otpInputTarget.value.trim()
    const phoneNumber = this.phoneInputTarget.value.trim()

    if (!otpCode || otpCode.length !== 6) {
      this.showError("Please enter the 6-digit verification code")
      return
    }

    this.setLoading(this.confirmButtonTarget, true, "Verifying...")

    try {
      const response = await fetch('/profile/confirm_phone_otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ 
          otp_code: otpCode,
          phone_number: phoneNumber
        })
      })

      const result = await response.json()

      if (result.success) {
        this.hideOtpModal()
        this.showSuccess(result.message)
        // Reload page to show verified status
        setTimeout(() => window.location.reload(), 1500)
      } else {
        this.showError(result.error)
      }
    } catch (error) {
      this.showError("Network error. Please try again.")
    } finally {
      this.setLoading(this.confirmButtonTarget, false, "Verify")
    }
  }

  async resend(event) {
    event.preventDefault()
    
    const phoneNumber = this.phoneInputTarget.value.trim()

    this.setLoading(this.resendButtonTarget, true, "Sending...")

    try {
      const response = await fetch('/profile/resend_phone_otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ phone_number: phoneNumber })
      })

      const result = await response.json()

      if (result.success) {
        this.showSuccess(result.message)
        this.otpInputTarget.value = ""
        this.otpInputTarget.focus()
      } else {
        this.showError(result.error)
      }
    } catch (error) {
      this.showError("Network error. Please try again.")
    } finally {
      this.setLoading(this.resendButtonTarget, false, "Resend Code")
    }
  }

  cancel(event) {
    event.preventDefault()
    this.hideOtpModal()
    this.otpInputTarget.value = ""
  }

  showOtpModal() {
    this.otpModalTarget.classList.remove("hidden")
    this.otpInputTarget.focus()
  }

  hideOtpModal() {
    this.otpModalTarget.classList.add("hidden")
  }

  setLoading(button, loading, text) {
    if (loading) {
      button.disabled = true
      button.innerHTML = `
        <svg class="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        ${text}
      `
    } else {
      button.disabled = false
      button.textContent = text
    }
  }

  showError(message) {
    this.showFlash(message, "error")
  }

  showSuccess(message) {
    this.showFlash(message, "success")
  }

  showFlash(message, type) {
    // Create flash message element
    const flash = document.createElement('div')
    flash.className = `fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
      type === 'error' ? 'bg-red-100 border border-red-400 text-red-700' : 'bg-green-100 border border-green-400 text-green-700'
    }`
    flash.textContent = message

    document.body.appendChild(flash)

    // Remove after 5 seconds
    setTimeout(() => {
      if (flash.parentNode) {
        flash.parentNode.removeChild(flash)
      }
    }, 5000)
  }
}